import { useMemo } from "react";
import { UseFirmList } from "../../hooks/Catalogs/useFirm";
import { UseCountryList } from "../../hooks/Catalogs/useCountry";
import { UsePracticeIndustryList } from "../../hooks/Catalogs/usePracticeIndustry";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import { MembershipStatus } from "../../Enums/FirmEnum";
import IFirm from "../../interfaces/models/IFirm";
import {
  LEGACY_MEMBER_SEARCH_REGIONS,
  type MemberSearchOption,
} from "./memberSearchQuery";
import { useMemberSearchOfficeLocations } from "./useMemberSearchOfficeLocations";
import { firmMemberSearchLabel } from "../../utils/firmDisplayName";

export const useMemberSearchCatalogOptions = (selectedFirmId?: string) => {
  const { firmData, firmsLoaded } = UseFirmList();
  const { countryData } = UseCountryList();
  const { practiceIndustryData } = UsePracticeIndustryList();
  const { regionData } = UseRegionList();

  const memberFirms = useMemo(
    () =>
      firmData
        .filter((firm: IFirm) => firm.membershipStatus === MembershipStatus.Member)
        .map((firm: IFirm) => ({
          id: firm.id,
          name: firmMemberSearchLabel(firm),
          legacyId: firm.legacyId,
          shortId: firm.shortId,
          jurisdiction: firm.jurisdiction,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [firmData],
  );

  const { officeLocations, isLoadingOfficeLocations } =
    useMemberSearchOfficeLocations(memberFirms, firmsLoaded, selectedFirmId);

  const regions = useMemo(() => {
    if (regionData.length) {
      return regionData.map((region: { id: string; name: string }) => ({
        id: String(region.id),
        name: region.name,
      }));
    }
    return LEGACY_MEMBER_SEARCH_REGIONS;
  }, [regionData]);

  const locations = useMemo(
    () =>
      countryData
        .map((country: { id: string; name: string }) => ({
          id: String(country.id),
          name: country.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [countryData],
  );

  const practiceIndustries = useMemo(
    () =>
      practiceIndustryData
        .map((industry: { id: string; name: string }) => ({
          id: industry.id,
          name: industry.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [practiceIndustryData],
  );

  const catalogs = useMemo(
    (): {
      firms: MemberSearchOption[];
      regions: MemberSearchOption[];
      locations: MemberSearchOption[];
      practiceIndustries: MemberSearchOption[];
      officeLocations: MemberSearchOption[];
    } => ({
      firms: memberFirms,
      regions,
      locations,
      practiceIndustries,
      officeLocations,
    }),
    [memberFirms, regions, locations, practiceIndustries, officeLocations],
  );

  return {
    memberFirms,
    regions,
    locations,
    practiceIndustries,
    officeLocations,
    isLoadingOfficeLocations,
    catalogs,
  };
};
